/**
 * Capture-phase submit handler. Runs BEFORE Webflow's native AJAX submit
 * because we attach at the document level with the capture flag. Order
 * of operations per submit:
 *   1. Match form against configured selectors.
 *   2. Honeypot check -> bail completely if triggered.
 *   3. Build attribution snapshot + bot-suspected flag.
 *   4. Populate hidden inputs so Webflow's webhook sees attribution.
 *   5. Fire fbq (only if Meta attribution), dataLayer, posthog.
 *   6. POST to /api/v1/forms/submit with keepalive.
 */

import { captureAttribution, capLength, LENGTH_CAPS, hasMetaAttribution } from './attribution';
import { populateHiddenInputs } from './attribution-fields';
import { isBotSuspectedByTiming, isHoneypotTriggered } from './bot';
import { pushDataLayer } from './datalayer';
import { trackFbq } from './fbq';
import { getGA4ClientId } from './ga4';
import type { FieldMapEntry, PixelConfig, PixelFormConfig } from './config';
import { posthogCapture, posthogDistinctId, posthogIdentify, posthogSessionId } from './posthog';
import { sha256Hex } from './sha256';
import { uuidv4 } from './uuid';

const META_PIXEL_VALUES: Record<string, { value: number; currency: string }> = {
  Lead: { value: 50, currency: 'USD' },
  CompleteRegistration: { value: 100, currency: 'USD' },
  Schedule: { value: 500, currency: 'USD' },
};

interface PixelRuntime {
  config: PixelConfig;
  getPageLoadTime: () => number | null;
}

export function installSubmitHandler(runtime: PixelRuntime): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const handler = (e: Event): void => {
    const target = e.target;
    if (!(target instanceof HTMLFormElement)) return;
    const formCfg = matchForm(target, runtime.config.forms);
    if (!formCfg) return;
    void handleSubmit(target, formCfg, runtime);
  };
  document.addEventListener('submit', handler, true);
  return () => document.removeEventListener('submit', handler, true);
}

function matchForm(form: HTMLFormElement, forms: PixelFormConfig[]): PixelFormConfig | undefined {
  for (const f of forms) {
    try {
      if (form.matches(f.selector)) return f;
    } catch {
      /* invalid selector */
    }
  }
  const rawName = form.getAttribute('data-name') || form.id || '';
  const formId = slugify(rawName);
  return {
    selector: 'form',
    formId,
    pixelEventName: 'Lead',
    dataLayerEventName: 'lead_submitted',
    posthogEventName: 'form_submitted',
    fieldMap: undefined,
    apiPost: undefined, // synthetic fallback defers to the global flag
  };
}

/**
 * Resolve whether the API POST fires for this form: per-form override wins,
 * otherwise the global config flag, defaulting to true. Only the POST is
 * gated — fbq/dataLayer/PostHog and hidden-input population always run, since
 * external transports (Zapier, PED-99) consume the hidden attribution inputs.
 */
function apiPostEnabled(config: PixelConfig, formCfg: PixelFormConfig): boolean {
  if (typeof formCfg.apiPost === 'boolean') return formCfg.apiPost;
  return config.apiPost !== false;
}

export function slugify(raw: string): string {
  const s = raw
    .toLowerCase()
    .trim()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return s || 'form';
}

async function handleSubmit(
  form: HTMLFormElement,
  formCfg: PixelFormConfig,
  runtime: PixelRuntime
): Promise<void> {
  try {
    // Honeypot: log + skip all firing. Do not preventDefault.
    if (isHoneypotTriggered(form)) {
      warn(runtime.config.debug, 'honeypot triggered for form ' + formCfg.formId);
      return;
    }
    const submitTime = Date.now();
    const botSuspected = isBotSuspectedByTiming(runtime.getPageLoadTime(), submitTime);
    const attribution = captureAttribution();
    const eventId = uuidv4();
    const timestampIso = new Date(submitTime).toISOString();

    // Populate hidden inputs synchronously so Webflow's FormData sees them.
    populateHiddenInputs(form, {
      attribution,
      eventId,
      formId: formCfg.formId,
      timestampIso,
      botSuspected,
    });

    const formData = new FormData(form);
    const data = mapFormData(formData, formCfg.fieldMap);
    const email = typeof data.email === 'string' ? data.email.trim().toLowerCase() : '';
    if (email) data.email = email;

    // 1. Meta Pixel (only when Meta attribution present)
    if (formCfg.pixelEventName && hasMetaAttribution()) {
      const meta = META_PIXEL_VALUES[formCfg.pixelEventName];
      const params: Record<string, unknown> = { content_name: formCfg.formId };
      if (meta) {
        params.value = meta.value;
        params.currency = meta.currency;
      }
      trackFbq(formCfg.pixelEventName, params, eventId, runtime.config.prodHosts);
    }

    // 2. Compute email hash once for dataLayer + PostHog identify
    const emailHash = email ? await safe(() => sha256Hex(email)) : undefined;

    // 3. dataLayer
    if (formCfg.dataLayerEventName) {
      pushDataLayer(
        omitUndefined({
          event: formCfg.dataLayerEventName,
          event_id: eventId,
          form: formCfg.formId,
          email_hash: emailHash,
        })
      );
    }

    // 4. PostHog capture + identify
    if (formCfg.posthogEventName) {
      posthogCapture(
        formCfg.posthogEventName,
        omitUndefined({ form: formCfg.formId, event_id: eventId, $insert_id: eventId })
      );
    }
    if (emailHash) {
      posthogIdentify(emailHash, { email_hash: emailHash });
    }

    // 5. POST to /api/v1/forms/submit with keepalive (skipped when apiPost
    // resolves false — e.g. Zapier owns server-side delivery, PED-99).
    if (apiPostEnabled(runtime.config, formCfg)) {
      const ga4ClientId = runtime.config.ga4MeasurementId
        ? await safe(() => getGA4ClientId(runtime.config.ga4MeasurementId as string, 500))
        : undefined;
      void postSubmission(runtime.config, formCfg, data, attribution, {
        eventId,
        ga4ClientId,
        timestampIso,
        botSuspected,
      });
    }
  } catch (err) {
    warn(runtime.config.debug, 'submit handler error: ' + (err as Error)?.message);
  }
}

interface PostExtras {
  eventId: string;
  ga4ClientId: string | undefined;
  timestampIso: string;
  botSuspected: boolean;
}

async function postSubmission(
  config: PixelConfig,
  formCfg: PixelFormConfig,
  data: Record<string, unknown>,
  attribution: ReturnType<typeof captureAttribution>,
  extras: PostExtras
): Promise<void> {
  try {
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
    const screenRes =
      typeof screen !== 'undefined' ? screen.width + 'x' + screen.height : undefined;
    const viewport =
      typeof window !== 'undefined' ? window.innerWidth + 'x' + window.innerHeight : undefined;
    const context = omitUndefined({
      event_id: capLength(extras.eventId, LENGTH_CAPS.eventId),
      posthog_distinct_id: capLength(posthogDistinctId(), LENGTH_CAPS.posthogDistinctId),
      posthog_session_id: capLength(posthogSessionId(), LENGTH_CAPS.posthogSessionId),
      utm_source: attribution.utm.utm_source,
      utm_medium: attribution.utm.utm_medium,
      utm_campaign: attribution.utm.utm_campaign,
      utm_term: attribution.utm.utm_term,
      utm_content: attribution.utm.utm_content,
      fbc: attribution.fbc,
      fbp: attribution.fbp,
      gclid: attribution.gclid,
      ga4_client_id: capLength(extras.ga4ClientId, LENGTH_CAPS.ga4ClientId),
      page_url: attribution.pageUrl,
      referrer: attribution.referrer,
      initial_referrer: capLength(attribution.initialReferrer, LENGTH_CAPS.initialReferrer),
      initial_search_engine: capLength(attribution.initialSearchEngine, LENGTH_CAPS.searchEngine),
      initial_search_query: capLength(attribution.initialSearchQuery, LENGTH_CAPS.searchQuery),
      session_id: attribution.sessionId,
      landing_url: attribution.landingUrl,
      submission_timestamp: extras.timestampIso,
      user_agent: userAgent,
      screen_resolution: screenRes,
      viewport_size: viewport,
      gbraid: attribution.gbraid,
      wbraid: attribution.wbraid,
      msclkid: attribution.msclkid,
      campaign_id: attribution.adIds?.campaign_id,
      medium_id: attribution.adIds?.medium_id,
      content_id: attribution.adIds?.content_id,
      ad_id: attribution.adIds?.ad_id,
      bot_suspected: extras.botSuspected,
    });
    const url = config.apiBaseUrl.replace(/\/+$/, '') + '/api/v1/forms/submit';
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ form: formCfg.formId, data, context }),
      keepalive: true,
    });
  } catch (err) {
    warn(config.debug, 'POST failed: ' + (err as Error)?.message);
  }
}

function mapFormData(
  fd: FormData,
  fieldMap: Record<string, FieldMapEntry> | undefined
): Record<string, unknown> {
  // Start with a pass-through of every form field except attribution / honeypot
  // entries. The fieldMap below is an OVERRIDE on top of this, so any Webflow
  // field the operator forgets to list still survives -- e.g. a "Department"
  // input added to the form does not silently disappear because the deployment
  // config was not updated.
  //
  // Empty + whitespace-only string values are skipped during pass-through so
  // the API payload does not ship keys with "" (which the backend would
  // persist verbatim and SF/Meta downstream mappers would not match).
  const out: Record<string, unknown> = {};
  const lowerCase = (k: string): string => k.toLowerCase();
  fd.forEach((value, key) => {
    const lower = lowerCase(key);
    if (lower.indexOf('attribution_') === 0) return;
    if (lower === '_honeypot') return;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') return;
      out[key] = trimmed;
      return;
    }
    out[key] = value;
  });
  if (!fieldMap) return out;

  for (const [source, entry] of Object.entries(fieldMap)) {
    const raw = fd.get(source);
    const value = typeof raw === 'string' ? raw.trim() : '';
    // Mapped entries always consume the original key so the payload does not
    // ship both "Work Email" AND "email" for the same value.
    delete out[source];
    if (typeof entry === 'string') {
      if (value) out[entry] = value;
      continue;
    }
    if ('split' in entry && Array.isArray(entry.split)) {
      // Guard against malformed split config: anything other than
      // [string, string] is silently dropped so a typo cannot end up
      // setting out["undefined"] = "..." in the API payload.
      const [firstKey, lastKey] = entry.split;
      if (typeof firstKey !== 'string' || typeof lastKey !== 'string') continue;
      if (!firstKey || !lastKey) continue;
      const parts = value.split(/\s+/).filter(Boolean);
      // Single-token names emit only the lastKey (the most-common backend
      // expectation is that lastName is required, firstName optional). The
      // empty firstName is dropped to stay consistent with the pass-through
      // pass above and omitUndefined elsewhere -- no key should ship as "".
      if (parts.length > 1) {
        out[firstKey] = parts.slice(0, -1).join(' ');
        out[lastKey] = parts[parts.length - 1];
      } else if (parts.length === 1) {
        out[lastKey] = parts[0];
      }
      continue;
    }
    if ('value' in entry) {
      const fixed = entry.fixes && value in entry.fixes ? entry.fixes[value] : value;
      if (fixed) out[entry.value] = fixed;
    }
  }
  return out;
}

function omitUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

async function safe<T>(fn: () => Promise<T> | T): Promise<T | undefined> {
  try {
    return await fn();
  } catch {
    return undefined;
  }
}

function warn(debug: boolean | undefined, msg: string): void {
  if (!debug) return;
  try {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[ignition-pixel] ' + msg);
    }
  } catch {
    /* noop */
  }
}
